// Drop protocol v1 — control messages on the 'ctl' channel (docs/phase-1-drop.md § Protocole).
// Everything here comes from another peer: parse defensively, never trust names or sizes.

export const CHUNK = 64 * 1024;
export const WINDOW = 8 * 1024 * 1024;
export const HIGH_WATER = 4 * 1024 * 1024;
export const LOW_WATER = 1024 * 1024;
export const ACK_EVERY = 2 * 1024 * 1024;
export const MAX_FILES = 500;

export type FileMeta = { id: string; name: string; size: number; type: string };

export type CtlMessage =
  | { t: 'hello'; name: string }
  | { t: 'manifest'; files: FileMeta[]; from: string }
  | { t: 'offer'; files: FileMeta[]; from: string }
  | { t: 'accept' }
  | { t: 'decline' }
  | { t: 'get'; file: string; offset: number }
  | { t: 'done'; file: string }
  | { t: 'cancel'; file?: string }
  | { t: 'error'; error: string };

const FILE_ID = /^[0-9a-f]{8,32}$/;

/** A display-safe file name: no path, no control characters, bounded length. */
export function safeName(raw: unknown): string {
  const name = String(raw ?? '')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .split(/[\\/]/)
    .pop()!
    .trim()
    .slice(0, 200);
  return name && name !== '.' && name !== '..' ? name : 'fichier';
}

export function safeLabel(raw: unknown, max = 60): string {
  return String(raw ?? '').replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, max) || 'appareil';
}

function fileMeta(raw: unknown): FileMeta | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  if (typeof f.id !== 'string' || !FILE_ID.test(f.id)) return null;
  if (typeof f.size !== 'number' || !Number.isSafeInteger(f.size) || f.size < 0) return null;
  const type = typeof f.type === 'string' && /^[\w.+-]+\/[\w.+-]+$/.test(f.type) ? f.type : 'application/octet-stream';
  return { id: f.id, name: safeName(f.name), size: f.size, type };
}

/** Returns a validated message, or null for anything malformed or unknown. */
export function parseCtl(raw: unknown): CtlMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  switch (m.t) {
    case 'hello':
      return { t: 'hello', name: safeLabel(m.name) };
    case 'manifest':
    case 'offer': {
      if (!Array.isArray(m.files) || m.files.length > MAX_FILES) return null;
      const files = m.files.map(fileMeta);
      if (files.some((f) => !f) || new Set(files.map((f) => f!.id)).size !== files.length) return null;
      return { t: m.t, files: files as FileMeta[], from: safeLabel(m.from) };
    }
    case 'accept':
    case 'decline':
      return { t: m.t };
    case 'get':
      if (typeof m.file !== 'string' || !FILE_ID.test(m.file)) return null;
      if (typeof m.offset !== 'number' || !Number.isSafeInteger(m.offset) || m.offset < 0) return null;
      return { t: 'get', file: m.file, offset: m.offset };
    case 'done':
      return typeof m.file === 'string' && FILE_ID.test(m.file) ? { t: 'done', file: m.file } : null;
    case 'cancel':
      return typeof m.file === 'string' && FILE_ID.test(m.file) ? { t: 'cancel', file: m.file } : { t: 'cancel' };
    case 'error':
      return { t: 'error', error: safeLabel(m.error, 200) };
    default:
      return null;
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} o`;
  const units = ['Ko', 'Mo', 'Go', 'To'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1).replace('.', ',') : Math.round(v)} ${units[i]}`;
}
